import isEqual from "lodash-es/isEqual";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import FormFieldTagsMagicBanner from "components/FormFields/Tags/MagicLabels";
import LabelsInfo from "components/LabelsInfo";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { getFullSpaceNamePath } from "utils/space";
import { SpacesContext } from "views/Account/SpacesProvider";
import useStackAvailableSpaces from "views/Stack/useStackAvailableSpaces";

import { StackFormContext } from "../context";
import NewStackFooter from "../Footer";
import { StackCreationWizardStep, StackDetailsFormFields } from "../types";
import useErrorHandlerNewStack from "../useErrorHandlerNewStack";
import useStackCreationAnalyticsVersion from "../useStackCreationAnalyticsVersion";
import { getTooltipAnalyticsProps } from "../utils";
import Documentation from "./Documentation";
import useHasEntities from "./useHasEntities";
import useNameAsyncValidation from "./useNameAsyncValidation";

const NewStackDetails = () => {
  const analyticsVersion = useStackCreationAnalyticsVersion();
  const [hasUnsubmittedLabelsChanges, setHasUnsubmittedLabelsChanges] = useState(false);

  const {
    asyncValidationLoading,
    currentStep,
    updateStepData,
    formData,
    setInternalFormData,
    internalData,
  } = useTypedContext(StackFormContext);
  const { spaces, error, spaceHierarchy } = useTypedContext(SpacesContext);

  const availableSpaces = useStackAvailableSpaces();

  useErrorHandlerNewStack(error);

  const stepData = formData[StackCreationWizardStep.Details];

  const builderForm = useForm<StackDetailsFormFields>({
    defaultValues: {
      ...stepData,
    },
    mode: "onChange",
  });

  const {
    register,
    control,
    formState: { errors },
    watch,
  } = builderForm;

  const formValidations = useFormValidations(builderForm);
  const runFormValidations = async () => {
    const isValid = await formValidations();
    return isValid && !hasUnsubmittedLabelsChanges;
  };

  const newStepData = watch();
  const validateNameAsync = useNameAsyncValidation();

  const { entitiesLoading, hasEntities } = useHasEntities();

  const processStepData = async () => {
    const spaceSelectOption = availableSpaces.find(({ id }) => id === newStepData.space);

    setInternalFormData({
      ...internalData,
      spaceLabel: spaceSelectOption
        ? getFullSpaceNamePath(spaceSelectOption.id, spaceHierarchy, spaces ?? [])
        : "",
    });

    return updateStepData(currentStep, newStepData);
  };

  const isDataChanged = !isEqual(newStepData, stepData);

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Add stack details"
          description="Name your stack, choose the space that it will belong to and add labels for improved organization and filtering"
        />

        <Box direction="column" margin="0 0 large">
          <FormProvider {...builderForm}>
            <Controller
              name="name"
              control={control}
              rules={{
                validate: validateNameAsync,
              }}
              render={({ field, fieldState }) => (
                <FormField
                  label="Name"
                  error={errors?.name?.message}
                  tooltipInfoVariant="modal"
                  {...getTooltipAnalyticsProps("Details", "Name", { version: analyticsVersion })}
                  tooltipInfo={
                    <>
                      <TooltipModalTitle>Name your stack</TooltipModalTitle>
                      <TooltipModalBody align="start">
                        Name of this stack. It should be unique within one account, and we recommend
                        that it closely represents the body of resources managed by this stack.
                        <ReadMoreDocsLink
                          docsUrl={getDocsUrl("/concepts/stack/creating-a-stack#1-stack-details")}
                        />
                      </TooltipModalBody>
                    </>
                  }
                >
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Name your stack"
                      ref={field.ref}
                      error={!!fieldState.error}
                      type="text"
                      value={field.value}
                      onChange={field.onChange}
                      name="stackName"
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />

            <FormFieldSpace
              spaces={availableSpaces}
              loading={entitiesLoading}
              disabled={entitiesLoading || hasEntities}
              {...getTooltipAnalyticsProps("Details", "Space", { version: analyticsVersion })}
            />

            {hasEntities && (
              <Box margin="large 0 0 0" direction="column">
                <Banner variant="info" title="Changing space is disabled">
                  Detach your directly attached policies, contexts, integrations and roles if you
                  want to change the space.
                </Banner>
              </Box>
            )}
            <FormFieldTags
              label={
                <Box gap="small" align="center">
                  Labels
                  <Typography tag="span" variant="p-body3" color="secondary">
                    (Optional)
                  </Typography>
                  <LabelsInfo
                    analyticsPage={AnalyticsPageStack.StackNew}
                    analyticsTitle="Tooltip click"
                    analyticsProps={{
                      location: "Details",
                      name: "Labels",
                      version: analyticsVersion,
                    }}
                  />
                </Box>
              }
              tagName="label"
              name="labels"
              inputSize="regular"
              analyticsPage={AnalyticsPageStack.StackNew}
              analyticsProps={{ version: analyticsVersion }}
              onInputChange={(value) => setHasUnsubmittedLabelsChanges(!!value)}
            />

            <FormFieldTagsMagicBanner entityType="stack" margin="x-large 0 0" />

            <FormField
              label="Description"
              isOptional
              tooltipInfoVariant="modal"
              {...getTooltipAnalyticsProps("Details", "Description", { version: analyticsVersion })}
              tooltipInfo={
                <>
                  <TooltipModalTitle>Describe your stack</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    Human-friendly description of this stack - supports Markdown. This value is only
                    used to make life easier for you, the user. You can use this space to describe
                    your stack, provide useful links, or embed a cat GIF. The only limit is
                    yourself.
                    <ReadMoreDocsLink
                      docsUrl={getDocsUrl("/concepts/stack/stack-settings#name-and-description")}
                    />
                  </TooltipModalBody>
                </>
              }
            >
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter stack description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>
          </FormProvider>
        </Box>
      </FullScreenModalBody>
      <NewStackFooter
        isDataChanged={isDataChanged}
        loading={asyncValidationLoading}
        processStepData={processStepData}
        documentationLink={getDocsUrl("/concepts/stack")}
        documentationTitle="Stack"
        documentationBody={<Documentation />}
        runFormValidations={runFormValidations}
      />
    </>
  );
};

export default NewStackDetails;
